import { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

function CalendarPage() {

  document.title = 'Calendar';

  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [kanbanJobs, setKanbanJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchJobsAndSchedule = async () => {
      if (!user) return;

      setLoading(true);
      try {
        // Fetch jobs with produced_quantity, shipped_quantity, and inventory_quantity
        const { data: jobs, error: jobsError } = await supabase
          .from('jobs')
          .select(`
            id, po_number, part_number, description, expected_delivery_date, 
            estimated_running_days, shop_id, quantity_on_order, 
            produced_quantity, shipped_quantity, shops(id, name),
            parts!inner(part_number, inventory_quantity)
          `);
        if (jobsError) throw jobsError;

        // Fetch part-machine mappings
        const { data: mappings, error: mappingsError } = await supabase
          .from('part_machine_mapping')
          .select('part_number, machine_id, machines(name, shop_id)');
        if (mappingsError) throw mappingsError;

        const partMachineMap = mappings.reduce((acc, m) => {
          acc[m.part_number] = { machine_id: m.machine_id, machine_name: m.machines.name };
          return acc;
        }, {});

        // Separate jobs into scheduled events (with due dates) and KANBAN jobs (without due dates)
        const scheduledEvents = [];
        const kanbanJobsList = [];

        jobs.forEach(job => {
          const machine = partMachineMap[job.part_number] || { machine_id: null, machine_name: 'Unassigned' };
          if (job.expected_delivery_date) {
            // Jobs with a due date: Show only on the due date
            const dueDate = new Date(job.expected_delivery_date);
            scheduledEvents.push({
              id: job.id,
              title: `${job.po_number} - ${job.part_number} (${job.description}) - ${machine.machine_name}`,
              start: dueDate,
              end: dueDate,
              allDay: true,
              resource: { 
                shop: job.shops?.name || 'Unknown', 
                shop_id: job.shop_id, 
                machine_id: machine.machine_id,
                po_number: job.po_number,
                part_number: job.part_number,
                description: job.description,
                quantity_on_order: job.quantity_on_order,
                produced_quantity: job.produced_quantity,
                shipped_quantity: job.shipped_quantity,
                inventory: job.parts?.inventory_quantity || 0,
              },
            });
          } else {
            // Jobs without a due date (KANBAN): Add to KANBAN list
            kanbanJobsList.push({
              id: job.id,
              po_number: job.po_number,
              part_number: job.part_number,
              description: job.description,
              shop: job.shops?.name || 'Unknown',
              machine: machine.machine_name,
              quantity_on_order: job.quantity_on_order,
              produced_quantity: job.produced_quantity,
              shipped_quantity: job.shipped_quantity,
              inventory: job.parts?.inventory_quantity || 0,
            });
          }
        });

        setEvents(scheduledEvents);
        setKanbanJobs(kanbanJobsList);
      } catch (err) {
        console.error('Error fetching and scheduling jobs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobsAndSchedule();
  }, [user]);

  const handleEventClick = (event) => {
    setSelectedJob(event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
  };

  const navigateToJobPage = () => {
    if (selectedJob) {
      navigate(`/jobs/${selectedJob.resource.po_number}`);
      closeModal();
    }
  };

  const calculateProgressPercentage = (produced, inventory, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 0;
    const totalAvailable = (produced || 0) + (inventory || 0);
    return Math.min((totalAvailable / quantityOnOrder) * 100, 100);
  };

  const calculateShippedPercentage = (shipped, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 0;
    return Math.min((shipped / quantityOnOrder) * 100, 100);
  };

  if (loading) {
    return <div className="text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="p-4">
      <h1 className="text-3xl font-bold mb-6 text-gray-900">Job Scheduling Calendar</h1>
      <div style={{ height: '600px' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          defaultView="month"
          views={['month', 'week', 'day']}
          style={{ height: '100%' }}
          eventPropGetter={(event) => ({
            style: {
              backgroundColor: event.resource.shop_id === 1 ? '#4f46e5' : '#10b981', // Blue for NORTH, Green for SOUTH
              color: 'white',
              borderRadius: '5px',
              border: 'none',
              padding: '5px',
              fontSize: '14px',
            },
          })}
          tooltipAccessor={(event) => `${event.title} - Shop: ${event.resource.shop}`}
          onSelectEvent={handleEventClick}
        />
      </div>

      {/* KANBAN Jobs Section */}
      <div className="mt-6 bg-white rounded-lg p-6 border-2">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">KANBAN Jobs</h2>
        {kanbanJobs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PO Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty Ordered</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produced</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipped</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inventory</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Machine</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {kanbanJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.po_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.part_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={job.description}>{job.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.quantity_on_order}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.produced_quantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.shipped_quantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.inventory}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.shop}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.machine}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No KANBAN jobs found.</p>
        )}
      </div>

      {/* Job Details Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle border-2 transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Job Details
                  </Dialog.Title>
                  {selectedJob && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">PO Number:</span> {selectedJob.resource.po_number}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Part Number:</span> {selectedJob.resource.part_number}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Description:</span> {selectedJob.resource.description}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Due Date:</span> {moment(selectedJob.start).format('MM/DD/YYYY')}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Quantity Ordered:</span> {selectedJob.resource.quantity_on_order}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Quantity Produced:</span> {selectedJob.resource.produced_quantity}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Quantity Shipped:</span> {selectedJob.resource.shipped_quantity}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Inventory:</span> {selectedJob.resource.inventory}
                      </p>
                      <div className="mt-2">
                        <span className="text-sm font-medium text-gray-600">Progress:</span>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                          <div
                            className="bg-indigo-600 h-2.5 rounded-full"
                            style={{ width: `${calculateProgressPercentage(selectedJob.resource.produced_quantity, selectedJob.resource.inventory, selectedJob.resource.quantity_on_order)}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {calculateProgressPercentage(selectedJob.resource.produced_quantity, selectedJob.resource.inventory, selectedJob.resource.quantity_on_order).toFixed(0)}%
                        </span>
                      </div>
                      <div className="mt-2">
                        <span className="text-sm font-medium text-gray-600">Shipped:</span>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                          <div
                            className="bg-green-600 h-2.5 rounded-full"
                            style={{ width: `${calculateShippedPercentage(selectedJob.resource.shipped_quantity, selectedJob.resource.quantity_on_order)}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {calculateShippedPercentage(selectedJob.resource.shipped_quantity, selectedJob.resource.quantity_on_order).toFixed(0)}%
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Shop:</span> {selectedJob.resource.shop}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Machine:</span> {selectedJob.resource.machine_id ? selectedJob.title.split(' - ').pop() : 'Unassigned'}
                      </p>
                    </div>
                  )}
                  <div className="mt-6 flex justify-end space-x-4">
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
                      onClick={navigateToJobPage}
                    >
                      View Job Page
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default CalendarPage;